import { initializeApp } from "firebase/app";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore/lite";
import { getFunctions } from "firebase/functions";
import { getStorage } from "firebase/storage";

// Get config from URL (if present), else cookie/localStorage in development, else env vars
const getFirebaseConfig = () => {
  // 1) Highest precedence: URL query params (works across ports if passed)
  if (typeof window !== "undefined" && typeof URLSearchParams !== "undefined") {
    const params = new URLSearchParams(window.location.search);
    const urlApiKey = params.get("firebaseApiKey");
    const urlAuthDomain = params.get("firebaseAuthDomain");
    const urlProjectId = params.get("firebaseProjectId");
    const urlStorageBucket = params.get("firebaseStorageBucket");
    const urlMessagingSenderId = params.get("firebaseMessagingSenderId");
    const urlAppId = params.get("firebaseAppId");
    const urlDatabaseURL = params.get("firebaseDatabaseURL");

    const hasAllRequired = Boolean(
      urlApiKey &&
        urlAuthDomain &&
        urlProjectId &&
        urlStorageBucket &&
        urlMessagingSenderId &&
        urlAppId,
    );

    if (
      hasAllRequired &&
      urlApiKey &&
      urlAuthDomain &&
      urlProjectId &&
      urlStorageBucket &&
      urlMessagingSenderId &&
      urlAppId
    ) {
      return {
        apiKey: urlApiKey,
        authDomain: urlAuthDomain,
        databaseURL: urlDatabaseURL || undefined,
        projectId: urlProjectId,
        storageBucket: urlStorageBucket,
        messagingSenderId: urlMessagingSenderId,
        appId: urlAppId,
      };
    }
  }

  // 2) In non-production, allow .env test override via localStorage flag
  if (import.meta.env.VITE_APP_MODE !== "production") {
    const isTestEnvFlag =
      typeof localStorage !== "undefined" &&
      localStorage.getItem("test-environment") === "true";
    if (isTestEnvFlag) {
      return {
        apiKey: import.meta.env.VITE_TEST_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_TEST_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_TEST_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_TEST_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env
          .VITE_TEST_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_TEST_FIREBASE_APP_ID,
      };
    }
  }

  // 3) In dev, allow cookie/localStorage override (legacy)
  if (import.meta.env.VITE_APP_MODE === "development") {
    // Prefer cookie so it works across localhost ports (3000, 3001, ...)
    const cookieString = typeof document !== "undefined" ? document.cookie : "";
    const cookieMatch = cookieString
      ?.split("; ")
      .find((row) => row.startsWith("firebaseConfig="));
    if (cookieMatch) {
      try {
        const cookieValue = decodeURIComponent(cookieMatch.split("=")[1] || "");
        if (cookieValue) return JSON.parse(cookieValue);
      } catch {
        // ignore malformed cookie and fall back to localStorage/env
      }
    }

    const sessionConfig =
      typeof localStorage !== "undefined"
        ? localStorage.getItem("firebaseConfig")
        : null;
    if (sessionConfig) {
      try {
        return JSON.parse(sessionConfig);
      } catch {
        // ignore and fall through to env vars
      }
    }
  }

  return {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
    projectId: import.meta.env.VITE_FIREBASE_PROJECTID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGEBUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  };
};

const firebaseConfig = getFirebaseConfig();

const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);
const cloudFunctions = getFunctions(firebaseApp, "europe-west1");

export default firebaseApp;
export { auth, db, storage, cloudFunctions, firebaseConfig };
